/**
 * View component for active site billing report 
 *
 * Displays a single study from the 'byId' map in the study reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';


// import actions
import * as siteActions from '../../site/siteActions';
import * as studyActions from '../studyActions';
import * as studyUserActions from '../../studyUser/studyUserActions';

// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';

import { MonthYearInput } from '../../../global/components/forms';

import { DateTime } from 'luxon';
import _ from 'lodash';
class StudyDashboardCompletionReport extends Base {
  constructor(props) {
    super(props);
    this.state = {
      allRoles: []
      , studyUserFilter: {
          hideAdminUsers: false
          , role: []
          // , showCompleted: true
          // , showIncomplete: true
          // , showNonStarters: true
          , siteNumber: ''
          , status: ''
        }
      , nextStudyUserFilter: {}
      , nextStudyUserListArgs: []
      , siteNumbers: []
      , studyUserListArgs: ['~study', props.match.params.studyId] // 1.12
      // , studyUserListArgs: ['~study', props.match.params.studyId, 'siteNumber', '001'] // 1.12 testing
      // , studyUserListArgs: ['study-analytics', props.match.params.studyId]
      // , studyUserListArgs: ['study-analytics', studyAction.item._id, 'email', 'sangel@cnshealthcare.com'] // single user
      , monthYear: DateTime.local().toMillis()
    }
    this._bind(
      '_applyStudyUserFilter'
      , '_handleFormChange'
      , '_exportBillingReport'
      , '_exportAllSiteData'
    );
  }

  componentDidMount() {
    const { dispatch, history, loggedInUser, match, studyUserStore } = this.props;
    const allRoles = studyUserStore ? studyUserStore.availableRoles : {};

    // Are we sure we only want published courses? Will we ever want to see an archived course on a completion report?
    dispatch(studyUserActions.fetchListIfNeeded(...this.state.studyUserListArgs)).then(suAction => {
      if(!suAction.success) {
        alert("Error finding study users. Refresh the page to try again.")
      }
    })

    this.setState({
      allRoles: allRoles
      , nextStudyUserFilter: _.cloneDeep(this.state.studyUserFilter)
      , nextStudyUserListArgs: _.cloneDeep(this.state.studyUserListArgs)
    })

    dispatch(siteActions.fetchListIfNeeded('_group', match.params.studyId)).then(siteAction => {
      if(siteAction.success) {
        const siteNumbers = Array.from(new Set(siteAction.list.map(site => site.number))).sort();
        this.setState({ siteNumbers: siteNumbers })
      }
    });

    dispatch(studyActions.fetchSingleIfNeeded(match.params.studyId));

    // We need to fetch the logged in study user to check permissions.
    dispatch(studyUserActions.fetchListIfNeeded('_user', loggedInUser._id, '_group', match.params.studyId));
  }

  componentDidUpdate(prevProps, prevState) {
    // catch for state change and re-fetch StudyUsers if it happens
    if(prevState.studyUserListArgs !== this.state.studyUserListArgs) {
      this.props.dispatch(studyUserActions.fetchListIfNeeded(...this.state.studyUserListArgs)).then(suAction => {
        if(!suAction.success) {
          alert("Error finding study users. Refresh the page to try again.")
        }
      });
    }
  }

  componentWillUnmount() {
    this.props.dispatch(studyUserActions.invalidateList(...this.state.studyUserListArgs))
  }

  _exportBillingReport(
    monthlyActiveGroupedBySiteNumber
  ) {
    console.log("exporting billing report data as csv")

    const { studyStore  } = this.props;

    const selectedStudy = studyStore.selected.getItem();

    let csv = "Site,Billable,Total Users\n";
    Object.keys(monthlyActiveGroupedBySiteNumber).sort((a, b) => a - b).map(site => {
      let nextLine = site + ',';
      if(!isNaN(parseInt(site.split('-')[0]))) {
        nextLine += 'yes,';
      } else {
        nextLine += 'no,';
      }
      nextLine += monthlyActiveGroupedBySiteNumber[site].length + ',\n';
      csv += nextLine;
    })

    const filename = `${selectedStudy.name}_ACTIVE_SITE_BILLING_REPORT_${DateTime.fromMillis(this.state.monthYear).toFormat('MMM-yyyy')}.csv`
    // download as a file when the user clicks the button
    csv = 'data:text/csv;charset=utf-8,' + csv;
    let data = encodeURI(csv);
    let link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  }

  _exportAllSiteData(
    monthlyActiveFilteredSiteUsers
  ) {
    // the way that we generate the page below does not work very well for generating the csv.
    console.log("exporting all site data as csv")

    const { studyStore  } = this.props;

    const selectedStudy = studyStore.selected.getItem();


    let csv = "Site,Billable,User,Role\n";
    let sortedSites = _.sortBy(monthlyActiveFilteredSiteUsers, ['siteNumber', 'email'])
    sortedSites.map(su => {

      let nextLine = su.siteNumber + ',';
      if(!isNaN(parseInt(su.siteNumber.split('-')[0]))) {
        nextLine += 'yes,';
      } else {
        nextLine += 'no,';
      }
      nextLine += su.email + ',';
      nextLine += su.role + '\n';
      csv += nextLine;
    })

    const filename = `${selectedStudy.name}_ACTIVE_SITE_DATA_${DateTime.fromMillis(this.state.monthYear).toFormat('MMM-yyyy')}.csv`
    // download as a file when the user clicks the button
    csv = 'data:text/csv;charset=utf-8,' + csv;
    let data = encodeURI(csv);
    let link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  }

  _handleFormChange(e) {
    let nextState = _.update( this.state, e.target.name, () => {
      return e.target.value;
    });
    this.setState(nextState);
  }

  _applyStudyUserFilter(studyUserList) {
    const { studyUserFilter } = this.state;

    let filteredStudyUserList = [...studyUserList]
    const filterKeys = Object.keys(studyUserFilter)
    // loop through each item in studyUserFilter.

    for(const key of filterKeys) {
      if(key === 'hideAdminUsers' && studyUserFilter[key] === true) {
        // Filter out every type of admin user.
        // filteredStudyUserList = filteredStudyUserList.filter(su => !su.admin && !su.viewAnalytics && !su.analytics.user.admin);
        filteredStudyUserList = filteredStudyUserList.filter(su => !su.admin && !su.permissions.includes('view-reports')); // double check this 
      } else if(Array.isArray(studyUserFilter[key])) {
        // Currently this only handles roles. Exclusionary by default. Return everything that isn't included in our filter array.
        // Only filter if the array is not empty.
        if(studyUserFilter[key].length > 0) {
          filteredStudyUserList = filteredStudyUserList.filter(su => !studyUserFilter[key].includes(su[key]))
        }
      } else if(studyUserFilter[key]) {
        filteredStudyUserList = filteredStudyUserList.filter(su => su[key] == [studyUserFilter[key]])
      }
    }
    return filteredStudyUserList

  }

  render() {
    const {
      location
      , loggedInUser
      , studyStore
      , studyUserStore
    } = this.props;

    /**
     * use the selected.getItem() utility to pull the actual study object from the map
     */
    const selectedStudy = studyStore.selected.getItem();

    // const studyUsers = studyUserStore.util.getList("_group", match.params.studyId) || [];
    const studyUsers = studyUserStore.util.getList(...this.state.studyUserListArgs) || [];
    const studyUsersListInfo = studyUserStore.util.getListInfo(...this.state.studyUserListArgs)

    // filter out entries with no user so they don't show up on the report.
    // const validStudyUsers = studyUsers && studyUsersListInfo && !studyUsersListInfo.isFetching ? studyUsers.filter(su => su.analytics && su.analytics.user) : []
    // todo: ^ they will want to show non-started users here too somehow
    // const filteredStudyUsers = this._applyStudyUserFilter(validStudyUsers);
    // console.log("debug 0", studyUsers.length)
    const filteredStudyUsers = this._applyStudyUserFilter(studyUsers);
    // console.log("debug 1", filteredStudyUsers.length)

    const monthlyActiveFilteredSiteUsers = filteredStudyUsers.filter(su => (DateTime.fromISO(su.created) <= DateTime.fromMillis(this.state.monthYear).endOf('month')) && (su.status == "active" || (su.dateRevoked && DateTime.fromISO(su.dateRevoked) > DateTime.fromMillis(this.state.monthYear) )));

    // console.log("debug 2", monthlyActiveFilteredSiteUsers.length);

    const monthlyActiveGroupedBySiteNumber = _.groupBy(monthlyActiveFilteredSiteUsers, 'siteNumber');
    // console.log("debug 3", Object.keys(monthlyActiveGroupedBySiteNumber).length)

    // Get the logged in user's study user object to check permissions.
    const loggedInStudyUserList = loggedInUser && selectedStudy ? studyUserStore.util.getList('_user', loggedInUser._id, '_group', selectedStudy._id) : null;
    // it's weird to just grab the first item in a 'list' like this, but this list should only ever have ONE item. If it has more then something is wrong.
    const loggedInStudyUser = loggedInStudyUserList && loggedInStudyUserList.length === 1 ? loggedInStudyUserList[0] : null;

    /**
     * TODO:  aggregate stats around how many study users HAVE logged in, started,
     * onboarded, completed, etc.
     */

    // console.log(countOnboarded);
    const isEmpty = (
      !selectedStudy
      || !selectedStudy._id
      || studyStore.selected.didInvalidate
    );

    const isAdmin = (
      loggedInStudyUser
      && loggedInStudyUser.admin
      || loggedInUser
      && loggedInUser.admin
    );

    const canViewAnalytics = (
      loggedInStudyUser
      && loggedInStudyUser.permissions.includes('view-reports')
      || isAdmin
    );

    return (
      <div className="flex">
        <Helmet
          title={"Site Billing Report | Pro-ficiency"}
        />
        <section className="section">
          <div className="yt-container fluid">
            { isEmpty ?
              (studyStore.selected.isFetching || !loggedInStudyUser ? <h2>Loading...</h2> : <h2>Empty.</h2>)
              :
              canViewAnalytics ?
              <div style={{ opacity: !studyUsersListInfo || studyUsersListInfo.isFetching ? 0.5 : 1 }}>
                <h2>{selectedStudy.name} </h2>
                <div className="yt-row with-gutters">
                  <div className="yt-col full s_50 m_30 l_25">
                    <div className="content-container">
                      <p><b>ACTIVE SITE BILLING REPORT</b></p>
                      <button type="button" className="yt-btn small danger"
                        onClick={ () => this._exportBillingReport(
                          monthlyActiveGroupedBySiteNumber
                        )}
                      >
                        Export Billing Report 
                      </button>
                      <button type="button" className="yt-btn small link danger"
                        onClick={ () => this._exportAllSiteData(
                          monthlyActiveFilteredSiteUsers
                        )}
                      >
                        Export Site Data
                      </button>
                      <h1>{Object.keys(monthlyActiveGroupedBySiteNumber).filter(k => !isNaN(parseInt(k.split(' - ')[0]))).length} - active sites</h1>
                      <hr/>
                      <p><strong>Active billed sites for the month of {DateTime.fromMillis(this.state.monthYear).toFormat('MMMM, yyyy')}</strong></p>
                      <MonthYearInput
                        change={this._handleFormChange}
                        helpText={`This study was created ${DateTime.fromISO(selectedStudy.created).toFormat('MM/yyyy')}`}
                        minDate={DateTime.fromISO(selectedStudy.created).toMillis()}
                        name="monthYear"
                        value={this.state.monthYear}
                      />
                      <div className="table-wrapper">
                        <table className="yt-table no-min">
                          <caption>All sites</caption>
                          <thead>
                            <tr>
                              <th className="">Billable</th>
                              <th className=" ">Site</th>
                              <th className=""># Users</th>
                            </tr>
                          </thead>
                          <tbody>
                            { _.sortBy(Object.keys(monthlyActiveGroupedBySiteNumber)).map((k, i) => 
                              <tr key={k + '_' + i}>
                                <td>
                                  {!isNaN(parseInt(k.split(' - ')[0])) && 
                                    <i className="fas fa-check-circle" />
                                  }
                                </td>
                                <td>{k}</td>
                                <td>{monthlyActiveGroupedBySiteNumber[k].length}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="yt-col full s_50 m_30 l_25">
                  </div>
                </div>
              </div>
              :
              <div>
                <h2>Unauthorized</h2>
                <p>You do not have permission to access this resource.</p>
                <Link to='/studies-dashboard'>Go home</Link>
              </div>
            }
          </div>
        </section>
      </div>
    )
  }
}

StudyDashboardCompletionReport.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    loggedInUser: store.user.loggedIn.user
    , studyStore: store.study
    , studyUserStore: store.studyUser
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(StudyDashboardCompletionReport)
);



// WEBPACK FOOTER //
// modules/study/views/StudyDashboardActiveSiteBillingReport.js.jsx